// CodingProfiles.jsx
import React from "react";
import { motion } from "framer-motion";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

const cardBase =
  "group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 transition-all duration-300 hover:-translate-y-2 hover:border-yellow-400/30";

const CodingProfiles = () => {
  return (
    <section className="relative overflow-hidden bg-custombg py-24 px-6">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-[450px] w-[450px] rounded-full bg-purple-500/20 blur-[120px]" />
        <div className="absolute -bottom-24 -right-24 h-[500px] w-[500px] rounded-full bg-blue-500/20 blur-[140px]" />
        <div className="absolute top-1/3 left-1/2 h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-pink-500/20 blur-[110px]" />
        <div className="absolute top-16 right-1/4 h-[220px] w-[220px] rounded-full bg-cyan-400/20 blur-[100px]" />
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:40px_40px]" />
      {/* <div className="absolute inset-0 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" /> */}

      <div className="relative mx-auto max-w-7xl">
        <div className="mb-16 text-center">
          <p className="uppercase tracking-[0.35em] text-yellow-400 font-semibold">
            Coding Journey
          </p>

          <h2 className="mt-4 text-5xl font-bold text-white">
            Coding <span className="text-yellow-400">Profiles</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-gray-400">
            Consistently solving Data Structures & Algorithms problems to
            improve analytical thinking and coding skills.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* LeetCode */}
          <motion.div whileHover={{ y: -8 }} className={cardBase}>
            <div className="mb-6 flex items-center justify-between">
              <span className="rounded-full bg-yellow-500/20 px-3 py-1 text-xs font-semibold text-yellow-300">
                🟢 Active
              </span>
              <span className="text-sm text-gray-500">LeetCode</span>
            </div>

            <div className="flex items-center gap-4">
              <img
                src="https://assets.leetcode.com/users/avatars/avatar_1672649659.png"
                className="h-14 w-14 rounded-full border-2 border-yellow-400"
                alt=""
              />

              <div>
                <h3 className="text-xl font-bold text-white">
                  Pushkar Chaubey
                </h3>
                <p className="text-sm text-gray-400">pushkarcoding10</p>
              </div>

              <VerifiedRoundedIcon className="ml-auto text-blue-400" />
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                <h2 className="text-4xl font-bold text-yellow-400">485+</h2>
                <p className="mt-1 text-sm text-gray-400">Problems Solved</p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                <h2 className="text-3xl font-bold text-green-400">Daily</h2>
                <p className="mt-1 text-sm text-gray-400">Practice</p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {["Data Structures", "Algorithms", "Problem Solving"].map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-sm text-yellow-300"
                >
                  {t}
                </span>
              ))}
            </div>

            <p className="mt-8 text-gray-400">
              Regularly practicing DSA to strengthen logical thinking and
              interview preparation.
            </p>

            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <EmojiEventsRoundedIcon className="text-yellow-400" />
                Active DSA Practice
              </div>

              <a
                href="https://leetcode.com/u/pushkarcoding10/"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 rounded-xl bg-yellow-400 px-5 py-3 font-semibold text-black"
              >
                Visit
                <OpenInNewRoundedIcon fontSize="small" />
              </a>
            </div>
          </motion.div>

          {/* GFG */}
          <motion.div whileHover={{ y: -8 }} className={cardBase}>
            <div className="mb-6 flex items-center justify-between">
              <span className="rounded-full bg-green-500/20 px-3 py-1 text-xs font-semibold text-green-300">
                🟢 Active
              </span>
              <span className="text-sm text-gray-500">GeeksForGeeks</span>
            </div>

            <div className="flex items-center gap-4">
              <img
                src="https://www.geeksforgeeks.org/_next/image/?url=https%3A%2F%2Fmedia.geeksforgeeks.org%2Fauth%2Fprofile%2Fr2nyq2ee3zb2hgq2znwn&w=256&q=75"
                className="h-14 w-14 rounded-full border-2 border-green-400"
                alt=""
              />

              <div>
                <h3 className="text-xl font-bold text-white">
                  Pushkar Chaubey
                </h3>
                <p className="text-sm text-gray-400">pushkarchaubey</p>
              </div>

              <VerifiedRoundedIcon className="ml-auto text-blue-400" />
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                <h2 className="text-4xl font-bold text-green-400">412+</h2>
                <p className="mt-1 text-sm text-gray-400">Problems Solved</p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                <h2 className="text-3xl font-bold text-cyan-400">Daily</h2>
                <p className="mt-1 text-sm text-gray-400">Coding</p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {["Algorithms", "Coding Practice", "DSA"].map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-green-500/30 bg-green-500/10 px-4 py-2 text-sm text-green-300"
                >
                  {t}
                </span>
              ))}
            </div>

            <p className="mt-8 text-gray-400">
              Solving algorithmic problems consistently and improving coding
              efficiency.
            </p>

            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <EmojiEventsRoundedIcon className="text-yellow-400" />
                Continuous Learning
              </div>

              <a
                href="https://www.geeksforgeeks.org/user/pushkarchaubey/"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 rounded-xl bg-green-500 px-5 py-3 font-semibold text-white"
              >
                Visit
                <OpenInNewRoundedIcon fontSize="small" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CodingProfiles;
